<script setup lang="ts">
import type { AuthorizationAction, TaskListSubject } from '~/types/authorization.type'
import type { NotificationThreshold } from '~/types/notification-threshold.type'
import type { Project } from '~/types/project.type'
import type { TaskList } from '~/types/taskList.type'
import { subject } from '@casl/ability'
import { unknownError } from '~/constants'

const props = defineProps({
  title: {
    type: String,
    default: 'Aufgabenliste erstellen',
  },
  project: {
    type: Object as PropType<Project<'withLeads'>>,
    required: true,
  },
  taskList: {
    type: Object as PropType<TaskList<'withTasks' | 'withProject' | 'withNotificationThresholds'>>,
    default: undefined,
  },
  name: {
    type: String,
    default: undefined,
  },
})

const emit = defineEmits<{
  taskListCreated: [taskList: TaskList<'withTasks'>]
  taskListEdited: [taskList: TaskList<'withTasks'>]
  taskListDeleted: []
  close: [boolean]
}>()

const model = ref<ApiTaskListCreateTaskList & {
  timeLimitHrs: number
  notificationThresholds: NotificationThreshold[]
}>({
  name: props.taskList?.name ?? props.name ?? '',
  projectId: props.project.id,
  isCompleted: props.taskList?.isCompleted ?? false,
  timeLimitHrs: (props.taskList?.timeLimitSec ?? 0) / 3600,
  timeLimitSec: props.taskList?.timeLimitSec ?? 0,
  notificationThresholds: props.taskList?.notificationThresholds ?? [],
})

const { can } = useAppAbility()

const action = computed<AuthorizationAction>(() => props.taskList ? 'update' : 'create')

const canDeleteTaskList = computed(() => {
  if (!props.taskList)
    return false
  return can('delete', subject('task-list', props.taskList))
})

const canSetTimeLimit = computed(() => {
  return can(action.value, subject('task-list', { project: props.project } as TaskListSubject), 'timeLimitSec')
})

const canSetNotificationThresholds = computed(() => {
  return can(action.value, subject('task-list', { project: props.project } as TaskListSubject), 'notificationThresholds')
})

const canSetCompleted = computed(() => {
  return can(action.value, subject('task-list', { project: props.project } as TaskListSubject), 'isCompleted')
})

const toast = useToast()
const loadingBar = useNaiveLoadingBar()

const modelToSend = computed<ApiTaskListCreateTaskList>(() => {
  return {
    isCompleted: canSetCompleted.value ? model.value.isCompleted : undefined,
    name: model.value.name,
    projectId: model.value.projectId,
    timeLimitSec: canSetTimeLimit.value ? model.value.timeLimitHrs * 3600 : undefined,
    notificationThresholds: canSetNotificationThresholds.value ? model.value.notificationThresholds : undefined,
  }
})

const { execute: executeTaskListCreate, error: taskListCreateError, data: taskListCreateData } = useApiTaskListCreate(modelToSend)

async function createTaskList() {
  loadingBar.start()
  await executeTaskListCreate()
  loadingBar.finish()

  if (!taskListCreateError.value && taskListCreateData.value) {
    emit('taskListCreated', taskListCreateData.value.data)
    toast.add({ color: 'success', description: 'Aufgabenliste erfolgreich erstellt!' })
    emit('close', false)
  }
  else if (taskListCreateError.value) {
    toast.add({ color: 'error', description: taskListCreateError.value.data?.message ?? unknownError })
  }
}

const { execute: executeTaskListEdit, error: taskListEditError, data: taskListEditData } = useApiTaskListUpdateById(props.taskList?.id ?? 0, modelToSend)

const { formRef, edited, reset, onSubmit, rules, pending, apiErrors } = useNaiveForm(model)
const { apiErrorMessages, clearApiErrors } = useApiErrors<ApiTaskListCreateTaskList>(apiErrors, taskListCreateError, taskListEditError)

rules.value = {
  name: [
    {
      required: true,
      message: 'Bitte einen Namen angeben',
      trigger: ['input', 'blur'],
    },
    {
      validator: () => !apiErrors.value.name,
      message: () => apiErrorMessages.value.name!,
      trigger: ['input', 'blur'],
    },
  ],
}

async function editTaskList() {
  loadingBar.start()
  await executeTaskListEdit()
  loadingBar.finish()

  if (!taskListEditError.value && taskListEditData.value) {
    emit('taskListEdited', taskListEditData.value.data)
    toast.add({ color: 'success', description: 'Aufgabeliste erfolgreich aktualisiert!' })
    emit('close', false)
  }
  else if (taskListEditError.value) {
    toast.add({ color: 'error', description: taskListEditError.value.data?.message ?? unknownError })
  }
}

function submitForm() {
  clearApiErrors()
  onSubmit(() => props.taskList ? editTaskList() : createTaskList())
}

useMetaSShortcut(submitForm)

const { openTaskListDeleteDialog } = useTaskListActions()
</script>

<template>
  <UModal
    :close="{ onClick: () => emit('close', false) }"
    :title="title"
    :dismissible="false"
  >
    <template #body>
      <NForm ref="formRef" :model="model" :rules="rules" class="md:grid grid-cols-2 gap-x-5" @submit.prevent="submitForm()">
        <NFormItem path="name" label="Name">
          <NInput v-model:value="model.name" placeholder="Aufgabenliste benennen" />
        </NFormItem>
        <NFormItem
          v-if="canSetCompleted"
          label="Status"
          path="isCompleted"
        >
          <TaskListCompletedSwitch
            v-model:value="model.isCompleted"
          />
        </NFormItem>
        <NFormItem v-if="canSetTimeLimit" label="Zeitlimit (Std)" path="timeLimitHrs" class="items-start">
          <InputNumber
            v-model:value="model.timeLimitHrs"
            :min="0"
            class="w-full"
            placeholder="Zeitlimit in Stunden"
          />
        </NFormItem>
        <NFormItem
          v-if="model.timeLimitHrs !== 0 && canSetNotificationThresholds"
          label="Benachrichtigung bei Schwellwerten" path="notificationThresholds"
        >
          <NotificationThresholdSelection v-model="model.notificationThresholds" />
        </NFormItem>
        <NFormItem class="col-span-2" :show-feedback="false">
          <div class="flex justify-end w-full">
            <NButtonGroup>
              <NTooltip>
                <template #trigger>
                  <NButton
                    type="success" :loading="pending"
                    :disabled="pending || (taskList && !edited)" attr-type="submit"
                  >
                    {{ `Aufgabenliste ${taskList ? 'aktualisieren' : 'erstellen'}` }}
                  </NButton>
                </template>
                <ShortcutDisplaySave />
              </NTooltip>
              <NButton secondary :disabled="taskList && !edited" @click="() => taskList ? reset() : $emit('close', false)">
                {{ taskList ? 'Zurücksetzen' : 'Abbrechen' }}
              </NButton>
              <NButton v-if="canDeleteTaskList" secondary type="error" @click="openTaskListDeleteDialog(taskList!, () => ($emit('taskListDeleted'), $emit('close', false)))">
                Löschen
              </NButton>
            </NButtonGroup>
          </div>
        </NFormItem>
      </NForm>
    </template>
  </UModal>
</template>
